import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Motion";
import SectionHeader from "@/components/SectionHeader";
import CTABanner from "@/components/CTABanner";
import { useLang } from "@/i18n";
import { SEO } from "@/components/SEO";

const PageHeader = () => {
  const { t } = useLang();
  return (
    <section data-testid="services-header" className="bg-obsidian pb-24 pt-40">
      <SEO
        title="Virtual Business Support Services | VCK Business Lab"
        description="Business & executive support, translation & interpretation, multilingual client support, documents & projects, website building, and 24/7 priority assistance — from $30 USD/hour."
        path="/services"
      />
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <MaskedLine delay={0.1}>
          <span className="overline text-gold">{t("services.overline")}</span>
        </MaskedLine>
        <h1 className="mt-6 max-w-4xl font-serif text-4xl font-light leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>{t("services.line1")}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold">{t("services.line2")}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <span className="mt-8 block max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {t("services.intro")}
          </span>
        </MaskedLine>
        <MaskedLine delay={0.75}>
          <span data-testid="services-languages" className="mt-8 block font-mono text-sm uppercase tracking-[0.25em] text-gold">
            {t("services.languages")}
          </span>
        </MaskedLine>
        <MaskedLine delay={0.85}>
          <span data-testid="services-worldwide" className="mt-3 block font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
            {t("services.worldwide")}
          </span>
        </MaskedLine>
      </div>
    </section>
  );
};

const ServiceCard = ({ service: s, open, onToggle, index }) => {
  const { t } = useLang();
  const Icon = s.icon;
  const dark = s.premium;
  return (
    <Reveal delay={(index % 3) * 0.08} className={open ? "md:col-span-2 xl:col-span-3" : ""}>
      <div
        data-testid={`service-card-${s.slug}`}
        className={`relative h-full rounded-xl p-8 transition-all duration-300 ${
          dark
            ? "border border-gold/50 bg-obsidian shadow-2xl"
            : `border bg-white ${open ? "border-gold/50 shadow-xl" : "border-obsidian/10 hover:-translate-y-1 hover:border-gold/50 hover:shadow-xl"}`
        }`}
      >
        {dark && (
          <span
            data-testid="emergency-premium-badge"
            className="absolute end-6 top-6 rounded-full bg-gold px-4 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-obsidian"
          >
            Premium · 24/7
          </span>
        )}
        <span
          className={`flex h-12 w-12 items-center justify-center rounded-lg border ${
            dark ? "border-gold/40 bg-obsidian-card text-gold" : "border-gold/30 bg-alabaster text-gold-muted"
          }`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <h3 className={`mt-6 font-serif text-2xl leading-snug ${dark ? "text-white" : "text-obsidian"} pe-8`}>
          {s.title}
        </h3>
        <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{s.tagline}</p>
        {s.meta && (
          <p className={`mt-3 font-mono text-[11px] uppercase tracking-[0.2em] ${dark ? "text-gold" : "text-gold-muted"}`}>
            {s.meta}
          </p>
        )}
        <p data-testid={`service-price-${s.slug}`} className={`mt-5 font-mono text-sm tracking-wide ${dark ? "text-gold" : "text-gold-muted"}`}>
          {s.price}
        </p>
        {s.priceNote && (
          <p className={`mt-1 font-mono text-xs ${dark ? "text-gold/70" : "text-slate-400"}`}>{s.priceNote}</p>
        )}
        <ul className="mt-6 space-y-2.5">
          {s.featured.map((f) => (
            <li key={f} className={`flex items-start gap-2.5 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
              <Check className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-gold" : "text-gold-muted"}`} />
              {f}
            </li>
          ))}
        </ul>
        <button
          data-testid={`service-see-more-${s.slug}`}
          onClick={onToggle}
          className={`mt-7 inline-flex items-center gap-2 text-sm font-semibold transition-colors ${
            dark ? "text-gold hover:text-gold-bright" : "text-gold-muted hover:text-gold"
          }`}
        >
          {open ? t("services.showLess") : s.moreLabel}
          <ArrowRight className={`h-4 w-4 transition-transform duration-300 rtl:-scale-x-100 ${open ? "-rotate-90" : ""}`} />
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className={`mt-8 border-t pt-8 ${dark ? "border-white/10" : "border-obsidian/10"}`}>
                {s.groups.map((g, gi) => (
                  <div key={gi} className={gi > 0 ? "mt-8" : ""}>
                    {g.heading && (
                      <p className={`overline mb-4 ${dark ? "text-gold" : "text-gold-muted"}`}>{g.heading}</p>
                    )}
                    <ul className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
                      {g.items.map((item) => (
                        <li
                          key={item}
                          className={`flex items-start gap-2.5 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}
                        >
                          <Check className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${dark ? "text-gold/70" : "text-gold-muted/70"}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div
                  className={`mt-10 rounded-xl border border-gold/30 p-6 ${dark ? "bg-obsidian-card" : "bg-alabaster"}`}
                >
                  <p className={`overline ${dark ? "text-gold" : "text-gold-muted"}`}>{t("services.pricing")}</p>
                  <div className="mt-4 grid gap-x-10 gap-y-3 sm:grid-cols-2">
                    {s.pricing.map((p) => (
                      <div
                        key={p.label}
                        className={`flex items-baseline justify-between gap-4 border-b pb-2 ${
                          dark ? "border-white/10" : "border-obsidian/10"
                        }`}
                      >
                        <span className={`text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>{p.label}</span>
                        <span className={`whitespace-nowrap font-mono text-sm ${dark ? "text-gold" : "text-obsidian"}`}>
                          {p.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  {s.note && (
                    <p className={`mt-5 text-xs italic leading-relaxed ${dark ? "text-slate-400" : "text-slate-500"}`}>
                      {s.note}
                    </p>
                  )}
                </div>
                <Link
                  to={`/contact?support=${s.slug}`}
                  data-testid={`service-cta-${s.slug}`}
                  className="btn-gold mt-8"
                >
                  {s.cta} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

const CategoryGrid = () => {
  const { c } = useLang();
  const [open, setOpen] = useState(null);
  return (
    <section data-testid="service-categories-section" className="bg-alabaster py-24 lg:py-28">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <div className="grid items-start gap-8 md:grid-cols-2 xl:grid-cols-3">
          {c.services.map((s, i) => (
            <ServiceCard
              key={s.slug}
              service={s}
              index={i}
              open={open === s.slug}
              onToggle={() => setOpen(open === s.slug ? null : s.slug)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const OngoingPlans = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="ongoing-plans-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader
          testid="ongoing-plans"
          overline={t("services.plansOverline")}
          title={t("services.plansTitle")}
          copy={t("services.plansCopy")}
        />
        <div className="mt-16 grid gap-8 md:grid-cols-2 xl:grid-cols-4">
          {c.plans.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08} className="h-full">
              <div
                data-testid={`plan-card-${p.slug}`}
                className={`card-lift relative flex h-full flex-col rounded-xl p-8 ${
                  p.popular
                    ? "border-2 border-gold bg-white shadow-xl"
                    : "border border-obsidian/10 bg-alabaster hover:border-gold/50 hover:shadow-lg"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3.5 start-8 rounded-full bg-gold px-4 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-obsidian">
                    {t("packages.mostRequested")}
                  </span>
                )}
                <h3 className="font-serif text-2xl text-obsidian">{p.name}</h3>
                <p data-testid={`plan-price-${p.slug}`} className="mt-3 font-mono text-sm tracking-wide text-gold-muted">
                  {p.price}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{p.fit}</p>
                <ul className="mt-7 flex-1 space-y-3 border-t border-obsidian/10 pt-7">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-muted" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/contact?plan=${p.slug}`}
                  data-testid={`plan-request-${p.slug}`}
                  className={`${p.popular ? "btn-gold" : "btn-outline-dark"} mt-8 w-full`}
                >
                  {p.cta}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingDisclaimer = () => {
  const { t } = useLang();
  return (
    <section data-testid="pricing-disclaimer-section" className="bg-alabaster py-20 lg:py-24">
      <div className="mx-auto max-w-3xl px-6 text-center sm:px-12">
        <Reveal>
          <p className="overline text-gold-muted">{t("services.disOverline")}</p>
          <p className="mt-6 font-serif text-xl italic leading-relaxed text-obsidian sm:text-2xl">
            {t("services.disQuote")}
          </p>
          <p className="mt-6 text-sm text-slate-500">{t("services.disNote")}</p>
        </Reveal>
      </div>
    </section>
  );
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader />
      <CategoryGrid />
      <OngoingPlans />
      <PricingDisclaimer />
      <CTABanner />
    </div>
  );
}
